'use client'

import { useRef } from 'react'
import { usePathname } from 'next/navigation'
import { useGSAP } from '@gsap/react'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { gsapSetup } from '@/lib/animations/gsapSetup'
import { useReducedMotion } from '@/lib/animations/useReducedMotion'

/**
 * FollowingRoot — a minimal vine fixed to the left of the viewport that
 * physically GROWS in height as the user scrolls.
 *
 * The SVG itself stays fully drawn at all times (stem, branches and dirt
 * are painted in their final state). Growth is achieved by animating the
 * *container's height* from `0` to its full extent, with `overflow: hidden`
 * clipping anything below. So as you scroll:
 *
 *   scroll 0%          → container height 0            → vine invisible
 *   scroll 25%         → container height 22vh         → top quarter of vine
 *   scroll 50%         → container height 44vh         → half the vine
 *   end of Beat 04     → container height 88vh         → full vine + dirt
 *
 * Tip of the vine always sits exactly at the bottom of the container's
 * clip, so it tracks the user's scroll 1-for-1 — "Jack and the beanstalk
 * in reverse". Dirt mound sits at the very bottom of the SVG, so it
 * appears only when the container reaches its final height at the end of
 * Beat 04.
 */

const WIDTH  = 44
const LEFT   = 24
const VB_H   = 1000
const BRANCH_COUNT = 6
const TOP_INSET_VH    = 6
const BOTTOM_INSET_VH = 6
const FULL_HEIGHT_CSS = `calc(100vh - ${TOP_INSET_VH + BOTTOM_INSET_VH}vh)`

type Paths = {
  stemD: string
  branches: Array<{
    d: string
    originFrac: number
    berryX: number
    berryY: number
    radius: number
  }>
}

function mulberry32(seed: number) {
  return () => {
    seed |= 0
    seed = (seed + 0x6d2b79f5) | 0
    let t = Math.imul(seed ^ (seed >>> 15), 1 | seed)
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t
    return ((t ^ (t >>> 14)) >>> 0) / 4_294_967_296
  }
}

function generatePaths(): Paths {
  const rng = mulberry32(1234)
  const cx = WIDTH / 2
  const STEM_END = VB_H - 18

  const segs: string[] = [`M ${cx} 0`]
  let y = 0
  while (y < STEM_END - 1) {
    const dy = 140 + rng() * 110
    const nextY = Math.min(y + dy, STEM_END)
    const wobbleA = (rng() - 0.5) * 18
    const wobbleB = (rng() - 0.5) * 18
    const endWobble = y === 0 ? 0 : (rng() - 0.5) * 8
    const cp1x = cx + wobbleA
    const cp1y = y + dy * 0.33
    const cp2x = cx + wobbleB
    const cp2y = y + dy * 0.66
    const ex = cx + endWobble
    const ey = nextY
    segs.push(
      `C ${cp1x.toFixed(2)} ${cp1y.toFixed(2)}, ${cp2x.toFixed(2)} ${cp2y.toFixed(2)}, ${ex.toFixed(2)} ${ey.toFixed(2)}`,
    )
    y = nextY
  }
  segs.push(`L ${cx.toFixed(2)} ${VB_H.toFixed(2)}`)

  const branches: Paths['branches'] = []
  const startY = STEM_END * 0.06
  const endY   = STEM_END * 0.92
  const span   = endY - startY
  for (let i = 0; i < BRANCH_COUNT; i++) {
    const base = startY + (span * (i + 0.5)) / BRANCH_COUNT
    const jitter = (rng() - 0.5) * (span / BRANCH_COUNT) * 0.6
    const originY = base + jitter
    const dir = rng() < 0.5 ? -1 : 1
    const length = 22 + rng() * 24
    const endX = cx + dir * length
    const bEndY = originY + 10 + rng() * 28
    const cpX = cx + dir * length * 0.4
    const cpY = originY + 5 + rng() * 6
    branches.push({
      d: `M ${cx} ${originY.toFixed(2)} Q ${cpX.toFixed(2)} ${cpY.toFixed(2)}, ${endX.toFixed(2)} ${bEndY.toFixed(2)}`,
      originFrac: originY / VB_H,
      berryX: endX,
      berryY: bEndY,
      radius: 2.4 + rng() * 0.9,
    })
  }
  branches.sort((a, b) => a.originFrac - b.originFrac)

  return { stemD: segs.join(' '), branches }
}

const PATHS = generatePaths()

export default function FollowingRoot() {
  // Vine grows by tracking the home page's #hero. Bail on other routes
  // so it doesn't appear (and try to query a missing element) on
  // /tech, /tecnologia, /about, etc.
  const pathname = usePathname()
  if (pathname !== '/') return null

  return <FollowingRootInner />
}

function FollowingRootInner() {
  const rootRef = useRef<HTMLDivElement>(null)
  const reduced = useReducedMotion()

  useGSAP(
    () => {
      gsapSetup()

      const el = rootRef.current
      if (!el) return

      const problemEl = document.querySelector<HTMLElement>('#problem')
      const strokes    = el.querySelectorAll<SVGPathElement>('[data-stroke]')

      // Reduced motion: just show the vine at full size, no scroll-driven
      // growth.
      if (reduced) {
        gsap.set(el, { height: FULL_HEIGHT_CSS })
        return
      }

      // Start collapsed at height 0 — nothing visible.
      gsap.set(el, { height: 0 })

      // ── Scroll range = Hero height + Problem's pin distance ──────────
      // Computed as a function so it re-evaluates on refresh (viewport
      // resize, pin-spacer insertion, etc.). This avoids the "ends too
      // soon" bug that happened when using `endTrigger: #solution` —
      // Solution's document Y is wrong at trigger-creation time because
      // Problem's pin-spacer hasn't been injected yet.
      //
      // Problem's pin distance: `useProblemScene` uses STAGE_SPAN=1.4 ×
      // 5 stages = 7 × 100% of viewport height = 7 screens of scroll.
      const endPosition = () => {
        const hero   = document.querySelector<HTMLElement>('#hero')
        const heroH  = hero?.offsetHeight ?? window.innerHeight
        const pin    = 7 * window.innerHeight
        return `+=${heroH + pin}`
      }

      // ── Variable-speed growth across 5 chunks with different eases ──
      // Gives the line an accelerating / living feel instead of a flat
      // linear crawl. Timeline still spans 0→1 across the scroll range.
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: document.body,
          start: 'top top',
          end: endPosition,
          scrub: 0.3,
          invalidateOnRefresh: true,
        },
      })

      tl.fromTo(el,
        { height: 0 },
        { height: '18vh',          duration: 0.18, ease: 'power2.out' },
        0,
      )
      tl.to(el, { height: '38vh',  duration: 0.22, ease: 'sine.inOut' })
      tl.to(el, { height: '60vh',  duration: 0.22, ease: 'power1.in'  })
      tl.to(el, { height: '78vh',  duration: 0.20, ease: 'sine.inOut' })
      tl.to(el, { height: FULL_HEIGHT_CSS, duration: 0.18, ease: 'power2.out' })

      // ── Gentle side-to-side sway (independent of scroll) ─────────────
      // Not tied to scroll — constantly drifts so the vine feels alive.
      gsap.to(el, {
        x: 4,
        duration: 3.2,
        ease: 'sine.inOut',
        repeat: -1,
        yoyo: true,
      })

      // ── After Beat 04 the vine is released to scroll with the page ──
      // Beyond the growth range, we translate the element up by the same
      // amount the user keeps scrolling — so it reads like a document-
      // anchored object that simply scrolls up into Solution / Awards /
      // etc., despite the element being `position: fixed`.
      gsap.fromTo(el,
        { y: 0 },
        {
          y: () => {
            const hero  = document.querySelector<HTMLElement>('#hero')
            const heroH = hero?.offsetHeight ?? window.innerHeight
            const pinEnd = heroH + 7 * window.innerHeight
            return -(ScrollTrigger.maxScroll(window) - pinEnd)
          },
          ease: 'none',
          immediateRender: false,
          scrollTrigger: {
            trigger: document.body,
            start: () => {
              const hero  = document.querySelector<HTMLElement>('#hero')
              const heroH = hero?.offsetHeight ?? window.innerHeight
              return heroH + 7 * window.innerHeight
            },
            end: 'max',
            scrub: 0,
            invalidateOnRefresh: true,
          },
        },
      )

      // Stroke color lightens across Problem so the vine stays readable
      // on the darker backdrop.
      if (problemEl) {
        gsap.to(strokes, {
          stroke: '#CFD37D',
          ease: 'none',
          scrollTrigger: {
            trigger: problemEl,
            start: 'top top',
            end:   'bottom bottom',
            scrub: 0.5,
          },
        })
      }
    },
    { scope: rootRef, dependencies: [reduced], revertOnUpdate: true },
  )

  return (
    <div
      ref={rootRef}
      aria-hidden="true"
      className="following-root"
      style={{
        // Fixed to the viewport and clipped by its own height. The SVG
        // inside is full-height; only the portion up to the container's
        // current height is visible.
        position: 'fixed',
        top: `${TOP_INSET_VH}vh`,
        left: LEFT,
        width: WIDTH,
        height: 0,               // animated by GSAP from 0 → FULL_HEIGHT_CSS
        overflow: 'hidden',
        zIndex: 35,
        pointerEvents: 'none',
        willChange: 'height',
      }}
    >
      <style>{`
        @media (max-width: 768px) {
          .following-root { display: none !important; }
        }
      `}</style>
      <svg
        viewBox={`0 0 ${WIDTH} ${VB_H}`}
        preserveAspectRatio="none"
        style={{
          display: 'block',
          width: '100%',
          // SVG stays at the full potential height while the container
          // clips it from the bottom as scroll progresses.
          height: FULL_HEIGHT_CSS,
          overflow: 'visible',
        }}
      >
        <path
          data-stroke
          d={PATHS.stemD}
          fill="none"
          stroke="#5B7005"
          strokeWidth={1.6}
          strokeLinecap="round"
          vectorEffect="non-scaling-stroke"
        />
        {PATHS.branches.map((b, i) => (
          <g key={i}>
            <path
              data-stroke
              d={b.d}
              fill="none"
              stroke="#5B7005"
              strokeWidth={1.2}
              strokeLinecap="round"
              vectorEffect="non-scaling-stroke"
            />
            <circle
              cx={b.berryX}
              cy={b.berryY}
              r={b.radius}
              fill="#9FAF08"
            />
          </g>
        ))}
        <DirtMound cx={WIDTH / 2} y={VB_H} />
      </svg>
    </div>
  )
}

function DirtMound({ cx, y }: { cx: number; y: number }) {
  return (
    <g>
      <ellipse cx={cx} cy={y - 1} rx="28" ry="3.2" fill="rgba(20,13,2,0.6)" />
      <path
        d={`M ${cx - 24} ${y - 3}
            Q ${cx - 16} ${y - 10}, ${cx - 9} ${y - 8}
            Q ${cx - 3} ${y - 12}, ${cx + 3} ${y - 10}
            Q ${cx + 11} ${y - 13}, ${cx + 18} ${y - 8}
            Q ${cx + 23} ${y - 11}, ${cx + 28} ${y - 3}
            L ${cx + 28} ${y}
            L ${cx - 24} ${y}
            Z`}
        fill="#3D2E0E"
      />
      <path
        d={`M ${cx - 24} ${y - 3}
            Q ${cx - 16} ${y - 10}, ${cx - 9} ${y - 8}
            Q ${cx - 3} ${y - 12}, ${cx + 3} ${y - 10}
            Q ${cx + 11} ${y - 13}, ${cx + 18} ${y - 8}
            Q ${cx + 23} ${y - 11}, ${cx + 28} ${y - 3}`}
        fill="none"
        stroke="#7A6130"
        strokeWidth={0.7}
        strokeOpacity={0.7}
      />
      <circle cx={cx - 14} cy={y - 6} r={0.7} fill="#513205" />
      <circle cx={cx - 4}  cy={y - 9} r={0.5} fill="#2A1E08" />
      <circle cx={cx + 6}  cy={y - 7} r={0.6} fill="#2A1E08" />
      <circle cx={cx + 16} cy={y - 10} r={0.5} fill="#513205" />
      <circle cx={cx + 22} cy={y - 5} r={0.6} fill="#2A1E08" />
    </g>
  )
}
